/**
 * Watch / spectate family — someone else's tournament game on our board.
 *
 * Functional spec = the OLD APP screen (line refs `src/Screens/PlayChess/
 * index.tsx`):
 *   watch_tournament_game_response          L3264-3321   (full snapshot)
 *   watch_player_turn_response              L3323-3361   (DELTA merge)
 *   watch_game_end_response                 L3363-3410
 *   stop_watching_tournament_game_response  L3449-3491   (~18 resets)
 *
 * THE DELTA IS THE TRAP. `watchTournamentGameResMsgSchema.moves` is a list of
 * OBJECTS (`{move, clock, color}`) and `watchPlayerTurnResMsgSchema.moves` is a
 * list of SAN STRINGS carrying only the NEW move, so the snapshot seeds a
 * tracker (`watchMovesSanRef`) and every turn appends to it. Losing the tracker
 * means the replay starts from the last single move.
 *
 * THE CLOCKS ARE IN DIFFERENT UNITS on the two messages: the snapshot's
 * `whiteTime`/`blackTime` are MILLISECONDS (the old screen divides by 1000,
 * L3302-3303) and the turn message's are SECONDS (it uses them raw, L3348-3349).
 * Both are normalised to ms here.
 *
 * Captured pieces are NOT taken from the wire: the server sends empty arrays for
 * schema compatibility, so they are derived from the watched FEN
 * (`selectWatchCaptured`) exactly as the app's old screen does (L3313-3320).
 */
import {
  LiveGameState,
  WatchPlayer,
  WatchSlice,
  id,
  initialWatch,
  WATCH_FINISHED_STATE,
} from '../state';

export const WATCH_TYPES = [
  'watch_tournament_game_response',
  'watch_player_turn_response',
  'watch_game_end_response',
  'stop_watching_tournament_game_response',
];

/** L3391-3395 — the end types the old screen scores as a draw. */
const DRAW_END_TYPES = ['draw', 'stalemate', 'insufficient', 'repetition'];

const withWatch = (
  state: LiveGameState,
  watch: Partial<WatchSlice>,
): LiveGameState => ({...state, watch: {...state.watch, ...watch}});

/**
 * A live watch event for a table we are NOT watching. The old screen has no
 * such guard, which is exactly the corruption its own comment warns about
 * ("stale events from the previous game corrupt the newly-watched board",
 * L5087-5090 — it works around it by sending `stop_watching` first).
 */
const otherWatchTable = (watch: WatchSlice, data: any): boolean =>
  watch.tableId !== null &&
  data?.tableId !== undefined &&
  Number(data.tableId) !== Number(watch.tableId);

/** the snapshot's `moves` are objects; a turn's are SAN strings. */
const sanList = (moves: any): string[] =>
  (Array.isArray(moves) ? moves : []).map(move =>
    move && typeof move === 'object' ? String(move.move) : String(move),
  );

const seat = (seatId: any, name: any, rating: any): WatchPlayer => ({
  id: id(seatId),
  name: name === undefined || name === null ? '' : String(name),
  rating: Number(rating) || null,
});

export function watchReducer(
  state: LiveGameState,
  type: string,
  data: any,
  now: number,
): LiveGameState {
  switch (type) {
    case 'watch_tournament_game_response':
      return watchSnapshot(state, data, now);
    case 'watch_player_turn_response':
      return watchTurn(state, data, now);
    case 'watch_game_end_response':
      return watchGameEnd(state, data);
    case 'stop_watching_tournament_game_response':
      return {...state, watch: {...initialWatch}};
    default:
      return state;
  }
}

/**
 * watch_tournament_game_response — the FULL snapshot that opens a watch
 * (L3264-3321). It replaces the slice wholesale (a previous watch's leftovers
 * must not survive) and seeds the SAN tracker.
 */
function watchSnapshot(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  return {
    ...state,
    watch: {
      ...initialWatch,
      active: true,
      tableId: data?.tableId === undefined ? null : Number(data.tableId),
      state: data?.state === undefined ? null : Number(data.state),
      fen: data?.gameState || null,
      prevFen: data?.prevGameState || null,
      startFen: data?.startFen || null,
      moves: sanList(data?.moves),
      white: seat(data?.white, data?.whiteUsername, data?.whiteRating),
      black: seat(data?.black, data?.blackUsername, data?.blackRating),
      // MILLISECONDS on this message (L3302-3303 divides by 1000).
      whiteTimeMs: Number(data?.whiteTime) || 0,
      blackTimeMs: Number(data?.blackTime) || 0,
      clockRunningSince: now,
      currentPlayer: id(data?.currentPlayer) || null,
    },
  };
}

/**
 * watch_player_turn_response — one move of the watched game (L3323-3361).
 * `moves` is the DELTA, appended to the tracked list.
 */
function watchTurn(
  state: LiveGameState,
  data: any,
  now: number,
): LiveGameState {
  // No watch in progress (never started, or torn down by a pairing / spawn /
  // tournament end): a straggler for the table we USED to watch must not
  // resurrect it — `otherWatchTable` alone cannot tell, its tableId is gone.
  if (!state.watch.active || otherWatchTable(state.watch, data)) {
    return state;
  }
  return withWatch(state, {
    active: true,
    moves: [...state.watch.moves, ...sanList(data?.moves)],
    fen: data?.data || state.watch.fen,
    prevFen: data?.previousFen || null,
    // SECONDS on this message (the old screen uses them raw, L3348-3349).
    whiteTimeMs: Number(data?.whiteTime || 0) * 1000,
    blackTimeMs: Number(data?.blackTime || 0) * 1000,
    clockRunningSince: now,
    currentPlayer: id(data?.userId) || null,
  });
}

/**
 * watch_game_end_response — `gameEndResMsgSchema`, so `moves` is the FULL list
 * (L3363-3410). State 8 is what stops the watched clocks; the 1.5 s modal delay
 * (L3407-3409) is the screen's, not the reducer's.
 */
function watchGameEnd(state: LiveGameState, data: any): LiveGameState {
  if (!state.watch.active || otherWatchTable(state.watch, data)) {
    return state;
  }
  const endType = data?.endType || '';
  let outcome: 'white' | 'black' | 'draw' | 'abort' | null;
  let message: string;
  if (DRAW_END_TYPES.includes(endType)) {
    outcome = 'draw';
    message = 'The match ended in a draw.';
  } else if (endType === 'abort') {
    outcome = 'abort';
    message = 'The match was aborted.';
  } else if (id(data?.winner) === id(data?.white)) {
    outcome = 'white';
    message = 'White won the match!';
  } else {
    outcome = 'black';
    message = 'Black won the match!';
  }
  const moves = sanList(data?.moves);
  return withWatch(state, {
    state: WATCH_FINISHED_STATE,
    fen: data?.gameState || state.watch.fen,
    moves: moves.length ? moves : state.watch.moves,
    clockRunningSince: null,
    ended: {endType, outcome, message},
  });
}
